import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import ExamInterface from './ExamInterface';
import {
  BookOpen,
  ArrowLeft,
  Zap,
  Infinity,
  Play,
  AlertTriangle,
  ChevronRight,
  Languages,
  Calculator,
  Monitor,
  Heart,
  Atom,
  FlaskConical,
  Dna,
  Sigma,
  Landmark,
  Globe,
  Scale,
  Microscope,
  FileText,
  Banknote,
  Briefcase,
  Sprout,
  Home as HomeIcon,
  Lock,
  Flag,
  FileQuestion,
  GraduationCap,
  Swords
} from 'lucide-react';
import BattleArena from './BattleArena';
import { type Subject, type Chapter } from '../../data/syllabus';
import { useShuddhoshikhaContext } from '../../hooks/useShuddhoshikhaContext';
import { useFavorites } from '../../hooks/useFavorites';
import { ShuddhoshikhaBreadcrumb, type BreadcrumbSegment } from './ShuddhoshikhaBreadcrumb';
import ProgressRing from '../ProgressRing';
import { normalizeCorrectAnswer } from '../../utils/answerNormalization';

interface QuestionSkeletonProps {
  qType: 'mcq' | 'cq';
}

function QuestionSkeleton({ qType }: QuestionSkeletonProps) {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-white/60 dark:bg-[#1E1E1E]/30 backdrop-blur-xl border border-white/20 dark:border-white/10 rounded-3xl p-6 shadow-[0_8px_32px_rgba(31,38,135,0.04)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.15)] space-y-5 text-left"
        >
          {/* Header */}
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-full skel-shimmer flex-shrink-0" />
            <div className="h-5 skel-shimmer rounded-lg w-1/3" />
          </div>

          {/* Body / Stem */}
          {qType === 'cq' ? (
            <div className="space-y-2.5 p-5 rounded-2xl bg-gray-100/50 dark:bg-white/5 border-l-4 border-l-gray-200 dark:border-l-white/20">
              <div className="h-3.5 skel-shimmer rounded w-full" />
              <div className="h-3.5 skel-shimmer rounded w-11/12" />
              <div className="h-3.5 skel-shimmer rounded w-4/5" />
            </div>
          ) : (
            <div className="h-3.5 skel-shimmer rounded w-10/12 ml-12" />
          )}

          {/* Options / Sub-questions */}
          <div className={`space-y-3.5 ${qType === 'cq' ? 'pl-4 md:pl-12' : 'pl-0 md:pl-12'}`}>
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full skel-shimmer flex-shrink-0" />
                <div className="h-3.5 skel-shimmer rounded w-2/3" />
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 dark:border-white/5 pt-4 pl-0 md:pl-12 flex justify-between items-center">
            <div className="h-3.5 skel-shimmer rounded w-16" />
            <div className="flex gap-4">
              <div className="w-8 h-4 skel-shimmer rounded" />
              <div className="w-8 h-4 skel-shimmer rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface ChapterWiseProps {
  onBack: () => void;
  isDarkMode?: boolean;
  onViewChange?: (view: string) => void;
}

type Phase = 'subjects' | 'chapters' | 'mode' | 'questionType' | 'practice' | 'battle';

const iconMap: Record<string, React.ElementType> = {
  BookOpen: BookOpen,
  Languages: Languages,
  Calculator: Calculator,
  Monitor: Monitor,
  Heart: Heart,
  Atom: Atom,
  FlaskConical: FlaskConical,
  Dna: Dna,
  Sigma: Sigma,
  Landmark: Landmark,
  Globe: Globe,
  Scale: Scale,
  Microscope: Microscope,
  FileText: FileText,
  Banknote: Banknote,
  Briefcase: Briefcase,
  Sprout: Sprout,
  Home: HomeIcon,
};

const getSubjectGradient = (subjectId: string): string => {
  const map: Record<string, string> = {
    sub_bn1: 'from-sky-400 to-blue-500 shadow-blue-500/15',
    sub_bn2: 'from-sky-400 to-blue-500 shadow-blue-500/15',
    sub_eng1: 'from-indigo-400 to-purple-500 shadow-purple-500/15',
    sub_eng2: 'from-indigo-400 to-purple-500 shadow-purple-500/15',
    sub_math: 'from-emerald-400 to-teal-500 shadow-teal-500/15',
    sub_ict: 'from-slate-400 to-slate-600 shadow-slate-500/15',
    sub_religion: 'from-rose-400 to-pink-500 shadow-rose-500/15',
    sub_phy: 'from-blue-500 to-indigo-600 shadow-indigo-500/15',
    sub_chem: 'from-cyan-400 to-teal-500 shadow-teal-500/15',
    sub_bio: 'from-green-400 to-emerald-500 shadow-emerald-500/15',
    sub_hmath: 'from-violet-500 to-purple-600 shadow-purple-500/15',
    sub_hist: 'from-amber-500 to-orange-600 shadow-orange-500/15',
    sub_geo: 'from-cyan-400 to-blue-500 shadow-blue-500/15',
    sub_civ: 'from-purple-400 to-indigo-500 shadow-indigo-500/15',
    sub_gsci: 'from-sky-400 to-blue-500 shadow-blue-500/15',
    sub_acc: 'from-teal-400 to-emerald-500 shadow-emerald-500/15',
    sub_fin: 'from-amber-400 to-yellow-500 shadow-amber-500/15',
    sub_bus: 'from-blue-400 to-indigo-500 shadow-indigo-500/15',
    opt_agri: 'from-green-400 to-emerald-500 shadow-emerald-500/15',
    opt_home: 'from-rose-400 to-pink-500 shadow-pink-500/15',
  };
  return map[subjectId] || 'from-sky-400 to-blue-500 shadow-blue-500/15';
};

// ── LocalStorage chapter progress persistence ─────────────────────────────────
const PROGRESS_KEY = 'cw_chapter_progress';

function loadProgress(): Record<string, Set<string>> {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return {};
    const parsed: Record<string, string[]> = JSON.parse(raw);
    return Object.fromEntries(
      Object.entries(parsed).map(([k, v]) => [k, new Set<string>(v)])
    );
  } catch {
    return {};
  }
}

function saveProgress(progress: Record<string, Set<string>>) {
  const serializable = Object.fromEntries(
    Object.entries(progress).map(([k, v]) => [k, Array.from(v)])
  );
  localStorage.setItem(PROGRESS_KEY, JSON.stringify(serializable));
}

const toBengaliNumber = (num: number | string): string => {
  const bn = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  return num.toString().replace(/\d/g, (d) => bn[parseInt(d)]);
};

function mapFrontendToDb(frontendSubject: string, frontendChapter: string): [string, string] {
  const subjectMap: Record<string, string> = {
    'sub_bn1': 'bangla',
    'sub_bn2': 'bangla-2nd',
    'sub_eng1': 'english',
    'sub_eng2': 'english-2nd',
    'sub_math': 'general-math',
    'sub_ict': 'ict',
    'sub_religion': 'religion',
    'sub_phy': 'physics',
    'sub_chem': 'chemistry',
    'sub_bio': 'biology',
    'sub_hmath': 'higher-math',
    'sub_hist': 'history',
    'sub_geo': 'geography',
    'sub_civ': 'civics',
    'sub_gsci': 'general-science',
    'sub_acc': 'accounting',
    'sub_fin': 'finance',
    'sub_bus': 'business',
    'opt_agri': 'agriculture',
    'opt_home': 'home-science'
  };

  const dbSubject = subjectMap[frontendSubject] || frontendSubject;
  let dbChapter = frontendChapter;

  if (frontendChapter) {
    // If the chapter ID already contains a hyphenated slug pattern from the backend
    // (e.g. 'bangla-ch1', 'physics-ch3'), pass it through as-is — it's already a DB ID
    const isBackendId = /^[a-z]+-ch\d+/.test(frontendChapter) ||
                        /^[a-z]+-\d+-ch\d+/.test(frontendChapter) ||
                        frontendChapter.includes('-ch');
    
    if (!isBackendId) {
      // Only apply prefix-based mapping for hardcoded frontend IDs
      if (frontendChapter.startsWith('b1_ch')) {
        dbChapter = frontendChapter.replace('b1_ch', 'bangla-ch');
      } else if (frontendChapter.startsWith('b2_ch')) {
        dbChapter = frontendChapter.replace('b2_ch', 'bangla-2nd-ch');
      } else if (frontendChapter.startsWith('e1_ch')) {
        dbChapter = frontendChapter.replace('e1_ch', 'english-ch');
      } else if (frontendChapter.startsWith('e2_ch')) {
        dbChapter = frontendChapter.replace('e2_ch', 'english-2nd-ch');
      } else if (frontendChapter.startsWith('m_ch')) {
        dbChapter = frontendChapter.replace('m_ch', 'general-math-ch');
      } else if (frontendChapter.startsWith('ict_ch')) {
        dbChapter = frontendChapter.replace('ict_ch', 'ict-ch');
      } else if (frontendChapter.startsWith('rel_ch')) {
        dbChapter = frontendChapter.replace('rel_ch', 'religion-ch');
      } else if (frontendChapter.startsWith('phy_ch')) {
        dbChapter = frontendChapter.replace('phy_ch', 'phys-ch');
      } else if (frontendChapter.startsWith('chem_ch')) {
        dbChapter = frontendChapter.replace('chem_ch', 'chem-ch');
      } else if (frontendChapter.startsWith('bio_ch')) {
        dbChapter = frontendChapter.replace('bio_ch', 'biology-ch');
      } else if (frontendChapter.startsWith('hm_ch')) {
        dbChapter = frontendChapter.replace('hm_ch', 'hmath-ch');
      } else if (frontendChapter.startsWith('hist_ch')) {
        dbChapter = frontendChapter.replace('hist_ch', 'history-ch');
      } else if (frontendChapter.startsWith('geo_ch')) {
        dbChapter = frontendChapter.replace('geo_ch', 'geography-ch');
      } else if (frontendChapter.startsWith('civ_ch')) {
        dbChapter = frontendChapter.replace('civ_ch', 'civics-ch');
      } else if (frontendChapter.startsWith('gs_ch')) {
        dbChapter = frontendChapter.replace('gs_ch', 'gen-science-ch');
      } else if (frontendChapter.startsWith('acc_ch')) {
        dbChapter = frontendChapter.replace('acc_ch', 'accounting-ch');
      } else if (frontendChapter.startsWith('fin_ch')) {
        dbChapter = frontendChapter.replace('fin_ch', 'finance-ch');
      } else if (frontendChapter.startsWith('bus_ch')) {
        dbChapter = frontendChapter.replace('bus_ch', 'business-ch');
      } else if (frontendChapter.startsWith('agr_ch')) {
        dbChapter = frontendChapter.replace('agr_ch', 'agriculture-ch');
      } else if (frontendChapter.startsWith('hs_ch')) {
        dbChapter = frontendChapter.replace('hs_ch', 'home-science-ch');
      }
    }
  }

  return [dbSubject, dbChapter];
}

export default function ChapterWise({ onBack, isDarkMode, onViewChange }: ChapterWiseProps) {
  const [phase, setPhase] = useState<Phase>(() => {
    try {
      const saved = localStorage.getItem('shuddhoshikha_battle_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Date.now() - parsed.timestamp < 2 * 60 * 60 * 1000) {
          return 'battle';
        }
      }
    } catch (e) {}
    
    // Fallback to query parameter logic
    const urlParams = new URLSearchParams(window.location.search);
    const hasBattleParam = urlParams.has('battle') || urlParams.has('practice_battle');
    if (hasBattleParam) {
      return 'battle';
    }
    return 'subjects';
  });

  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(() => {
    try {
      const saved = localStorage.getItem('shuddhoshikha_battle_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Date.now() - parsed.timestamp < 2 * 60 * 60 * 1000) {
          return { id: 'persisted', name: parsed.subjectName, chapters: [], icon: '' } as Subject;
        }
      }
    } catch (e) {}
    return null;
  });

  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(() => {
    try {
      const saved = localStorage.getItem('shuddhoshikha_battle_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Date.now() - parsed.timestamp < 2 * 60 * 60 * 1000) {
          return { id: 'persisted', name: parsed.chapterName, totalQuestions: 0 } as Chapter;
        }
      }
    } catch (e) {}
    return null;
  });

  const [selectedMode, setSelectedMode] = useState<string | null>(null);
  const [selectedQuestionType, setSelectedQuestionType] = useState<'mcq' | 'cq' | null>(null);
  const [expandedSubQuestions, setExpandedSubQuestions] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'ব্যাকরণ' | 'নির্মিতি'>('ব্যাকরণ');
  const [isSharedLink, setIsSharedLink] = useState(false);

  const shAuth = (window as any).shAuth;
  const { profile, subjects, isLoading: contextLoading } = useShuddhoshikhaContext();
  const { isFavorite, toggleFavorite } = useFavorites();

  const userOptional = profile?.sh_optional || null;

  const [questions, setQuestions] = useState<any[]>([]);
  const [showAnswers, setShowAnswers] = useState<boolean>(false);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [loadingQuestions, setLoadingQuestions] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [flaggedQuestionIds, setFlaggedQuestionIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    const handleFlagged = (e: any) => {
      if (e.detail?.questionId) {
        setFlaggedQuestionIds(prev => {
          const next = new Set(prev);
          next.add(e.detail.questionId);
          return next;
        });
      }
    };
    window.addEventListener('question-flagged' as any, handleFlagged);
    return () => window.removeEventListener('question-flagged' as any, handleFlagged);
  }, []);

  // Synchronize placeholder persisted/popped subject/chapter with real context data
  useEffect(() => {
    if (subjects && subjects.length > 0) {
      let currentSubject = selectedSubject;

      // 1. Sync Subject if it's a placeholder
      if (selectedSubject && (selectedSubject.id === 'persisted' || !subjects.some(s => s.id === selectedSubject.id && s.chapters && s.chapters.length > 0))) {
        const realSubject = selectedSubject.id === 'persisted'
          ? subjects.find(s => s.name === selectedSubject.name)
          : subjects.find(s => s.id === selectedSubject.id);
        if (realSubject) {
          setSelectedSubject(realSubject);
          currentSubject = realSubject;
        }
      }

      // 2. Sync Chapter if it's a placeholder
      if (selectedChapter && currentSubject) {
        const isChapterPlaceholder = selectedChapter.id === 'persisted' || 
          !(currentSubject.chapters && currentSubject.chapters.some(c => c.id === selectedChapter.id));
        
        if (isChapterPlaceholder) {
          const realChapter = selectedChapter.id === 'persisted'
            ? currentSubject.chapters?.find(c => c.name === selectedChapter.name)
            : currentSubject.chapters?.find(c => c.id === selectedChapter.id);
          if (realChapter) {
            setSelectedChapter(realChapter);
          }
        }
      }
    }
  }, [subjects, selectedSubject, selectedChapter]);

  // Fallback to safe phase if required data is missing
  useEffect(() => {
    if (phase === 'chapters' && !selectedSubject) {
      setPhase('subjects');
    } else if ((phase === 'mode' || phase === 'questionType' || phase === 'practice' || phase === 'battle') && (!selectedSubject || !selectedChapter)) {
      setPhase('subjects');
    }
  }, [phase, selectedSubject, selectedChapter]);

  // Automatically detect Battle invite link from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const battleCode = params.get('battle');
    const subjectIdParam = params.get('subject');
    const chapterIdParam = params.get('chapter');
    if (battleCode && subjects && subjects.length > 0) {
      const activeSub = subjects.find(s => s.id === subjectIdParam) || subjects[0];
      const activeChap = activeSub.chapters?.find(c => c.id === chapterIdParam) || activeSub.chapters?.[0] || { id: 'default', name: 'মমতাদি' };
      
      setSelectedSubject(activeSub);
      setSelectedChapter(activeChap);
      setPhase('battle');
      setIsSharedLink(true);
      
      // Clear the query parameter so refreshing doesn't loop infinitely
      const newUrl = window.location.pathname;
      window.history.replaceState({
        view: 'চর্চাঘর',
        subView: 'chapterWise',
        phase: 'battle',
        subjectId: activeSub.id,
        chapterId: activeChap.id
      }, document.title, newUrl);
    }
  }, [subjects]);

  // Chapter progress tracking (localStorage)
  const [chapterProgress, setChapterProgress] = useState<Record<string, Set<string>>>(loadProgress);

  const markChapterVisited = useCallback((subjectId: string, chapterId: string) => {
    setChapterProgress(prev => {
      const next = { ...prev };
      if (!next[subjectId]) next[subjectId] = new Set();
      next[subjectId] = new Set(next[subjectId]);
      next[subjectId].add(chapterId);
      saveProgress(next);
      return next;
    });
  }, []);

  const getSubjectProgress = useCallback((subjectId: string, totalChapters: number): number => {
    if (totalChapters === 0) return 0;
    const visited = chapterProgress[subjectId]?.size || 0;
    return Math.round((Math.min(visited, totalChapters) / totalChapters) * 100);
  }, [chapterProgress]);

  // Filters State
  const [selectedSegment, setSelectedSegment] = useState<'all' | 'board'>('all');
  const [selectedTopic, setSelectedTopic] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Dynamically extract unique years from questions list
  const availableYears = useMemo(() => {
    const yearsSet = new Set<string>();
    questions.forEach((q) => {
      if (q.exam_year) {
        const clean = String(q.exam_year).trim();
        if (clean && clean !== 'null' && clean !== 'undefined') {
          clean.split(/[,&]+/).forEach(y => {
            const yr = y.trim();
            if (/^\d{4}$/.test(yr)) {
              yearsSet.add(yr);
            }
          });
        }
      }
    });
    return Array.from(yearsSet).sort((a, b) => b.localeCompare(a));
  }, [questions]);

  // Dynamically extract unique topics from questions list
  const availableTopics = useMemo(() => {
    const topicsSet = new Set<string>();
    questions.forEach((q) => {
      if (q.topic_name) {
        topicsSet.add(q.topic_name.trim());
      }
    });
    return Array.from(topicsSet).sort();
  }, [questions]);

  // Reset filters when chapter or type changes
  useEffect(() => {
    setSelectedSegment('all');
    setSelectedTopic('all');
    setSelectedYear('all');
    setCurrentPage(1);
  }, [selectedChapter?.id, selectedQuestionType]);

  // Reset page number when specific filters are toggled
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSegment, selectedTopic, selectedYear]);

  // Filtered Questions list
  const filteredQuestions = useMemo(() => {
    return questions.filter((q) => {
      // 1. Segment filter ('all' vs 'board')
      if (selectedSegment === 'board') {
        const hasBoard = q.board_name || (q.exam_references && /"reference_board":/i.test(String(q.exam_references)));
        const hasBoardRef = q.exam_references && (() => {
          try {
            const refs = typeof q.exam_references === 'string' ? JSON.parse(q.exam_references) : q.exam_references;
            return Array.isArray(refs) && refs.some((ref: any) => ref.reference_board);
          } catch (e) {
            return false;
          }
        })();
        const isBoardStr = String(q.exam_references).toLowerCase().includes('board') || String(q.exam_references).toLowerCase().includes('dhaka') || String(q.exam_references).toLowerCase().includes('rajshahi') || String(q.exam_references).toLowerCase().includes('chittagong') || String(q.exam_references).toLowerCase().includes('jessore') || String(q.exam_references).toLowerCase().includes('comilla') || String(q.exam_references).toLowerCase().includes('dinajpur') || String(q.exam_references).toLowerCase().includes('barisal') || String(q.exam_references).toLowerCase().includes('sylhet') || String(q.exam_references).toLowerCase().includes('mymensingh') || String(q.exam_references).toLowerCase().includes('khulna') || String(q.exam_references).toLowerCase().includes('madrasah');

        if (!hasBoard && !hasBoardRef && !isBoardStr) {
          return false;
        }
      }

      // 2. Topic filter
      if (selectedTopic !== 'all') {
        if (!q.topic_name || q.topic_name.trim() !== selectedTopic) {
          return false;
        }
      }

      // 3. Year filter
      if (selectedYear !== 'all') {
        if (!q.exam_year || !String(q.exam_year).includes(selectedYear)) {
          return false;
        }
      }

      return true;
    });
  }, [questions, selectedSegment, selectedTopic, selectedYear]);

  const questionsPerPage = 50;
  const totalPages = Math.ceil(filteredQuestions.length / questionsPerPage);

  const paginatedQuestions = useMemo(() => {
    if (selectedMode !== 'unlimited') return filteredQuestions;
    const startIndex = (currentPage - 1) * questionsPerPage;
    return filteredQuestions.slice(startIndex, startIndex + questionsPerPage);
  }, [filteredQuestions, currentPage, selectedMode]);

  // Shuffle MCQ questions and take up to 20 for Quick Test mode
  const examQuestions = useMemo(() => {
    if (selectedMode !== 'quick') return questions;
    const mcqQuestions = questions.filter(q => q.type === 'mcq');
    const shuffled = [...mcqQuestions].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 20);
  }, [questions, selectedMode]);

  // Replace initial state with subjects phase
  useEffect(() => {
    const current = window.history.state;
    if (current && current.view === 'চর্চাঘর' && current.subView === 'chapterWise' && !current.phase) {
      window.history.replaceState({
        ...current,
        phase: 'subjects'
      }, '');
    }
  }, []);

  // Sync state changes to browser history
  useEffect(() => {
    const current = window.history.state;
    if (current && current.view === 'চর্চাঘর' && current.subView === 'chapterWise') {
      if (current.phase !== phase ||
          current.subjectId !== selectedSubject?.id ||
          current.chapterId !== selectedChapter?.id ||
          current.questionType !== selectedQuestionType) {
        window.history.pushState({
          view: 'চর্চাঘর',
          subView: 'chapterWise',
          phase,
          subjectId: selectedSubject?.id,
          chapterId: selectedChapter?.id,
          mode: selectedMode,
          questionType: selectedQuestionType
        }, '');
      }
    }
  }, [phase, selectedSubject, selectedChapter, selectedMode, selectedQuestionType]);

  // Listen to popstate event (browser back button)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state;
      if (state && state.view === 'চর্চাঘর' && state.subView === 'chapterWise') {
        if (state.phase) setPhase(state.phase);
        
        if (state.subjectId) {
          const sub = Array.isArray(subjects) ? subjects.find(s => s.id === state.subjectId) : null;
          setSelectedSubject(sub || { id: state.subjectId, name: '', chapters: [], icon: '' } as Subject);
        } else {
          setSelectedSubject(null);
        }

        if (state.chapterId && state.subjectId) {
          const sub = Array.isArray(subjects) ? subjects.find(s => s.id === state.subjectId) : null;
          const chap = sub?.chapters?.find(c => c.id === state.chapterId);
          setSelectedChapter(chap || { id: state.chapterId, name: '', totalQuestions: 0 } as Chapter);
        } else {
          setSelectedChapter(null);
        }

        setSelectedMode(state.mode || null);
        setSelectedQuestionType(state.questionType || null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [subjects]);

  // Reactive Polling Logic for questions on Chapter or Type changes
  // NOTE: The REST endpoint uses permission_callback => '__return_true', so no nonce is needed.
  useEffect(() => {
    if ((phase !== 'practice' && phase !== 'battle') || !selectedSubject || !selectedChapter || !selectedQuestionType) {
      return;
    }

    setLoadingQuestions(true);
    setFetchError(null);
    const [mappedSubject, chapterSlug] = mapFrontendToDb(selectedSubject.id, selectedChapter.id);
    const subjectSlug = (selectedSubject as any).db_slug || mappedSubject;
    const url = `/wp-json/shuddhoshikha/v1/questions?subject_slug=${encodeURIComponent(subjectSlug)}&chapter_slug=${encodeURIComponent(chapterSlug)}&question_type=${selectedQuestionType}&reveal_answers=true`;

    console.log(`[Shuddhoshikha] Polling: ${url}`);

    const headers: Record<string, string> = {
      'Accept': 'application/json'
    };
    if (shAuth?.rest_nonce) {
      headers['X-WP-Nonce'] = shAuth.rest_nonce;
    }

    fetch(url, {
      method: 'GET',
      headers: headers
    })
    .then(async (res) => {
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const text = await res.text();
      console.log(`[Shuddhoshikha] Response (${text.length} bytes):`, text.substring(0, 200));
      try {
        return JSON.parse(text);
      } catch (e) {
        throw new Error('Invalid JSON returned from server');
      }
    })
    .then(res => {
      const data = res.data || res.questions || [];
      if (res.status === 'success' || res.success || Array.isArray(data)) {
        const mappedQuestions = data.map((q: any) => {
          if (q.type === 'cq') {
            return {
              id: q.id,
              type: 'cq',
              stem: q.stem || q.stimulus || '',
              question_text: q.question_text || {
                'ক': q['ক'] || { text: '', marks: 1, sub_question_answer: '' },
                'খ': q['খ'] || { text: '', marks: 2, sub_question_answer: '' },
                'গ': q['গ'] || { text: '', marks: 3, sub_question_answer: '' },
                'ঘ': q['ঘ'] || { text: '', marks: 4, sub_question_answer: '' }
              },
              exam_year: q.exam_year || '2025',
              explanation: q.explanation || '',
              topic_name: q.topic_name || '',
              exam_references: q.exam_references || ''
            };
          } else {
            const optionsArray = Array.isArray(q.options) 
              ? q.options 
              : [q.option_a || '', q.option_b || '', q.option_c || '', q.option_d || ''];
            return {
              id: q.id,
              type: 'mcq',
              question_text: q.question_text || '',
              options: optionsArray,
              correct_answer: q.correct_answer !== undefined ? String(q.correct_answer) : String(q.correct_option !== undefined ? q.correct_option : '0'),
              exam_year: q.exam_year || '2025',
              explanation: q.explanation || '',
              topic_name: q.topic_name || '',
              exam_references: q.exam_references || ''
            };
          }
        });
        console.log(`[Shuddhoshikha] Loaded ${mappedQuestions.length} mapped questions`);
        setQuestions(mappedQuestions);
      } else {
        console.warn('[Shuddhoshikha] API returned empty or failed status', res);
        setQuestions([]);
        setFetchError('সার্ভার থেকে সঠিক প্রশ্ন পাওয়া যায়নি।');
      }
    })
    .catch((err) => {
      console.error('[Shuddhoshikha] Fetch questions error:', err);
      setQuestions([]);
      setFetchError(err.message || 'প্রশ্ন লোড করতে সমস্যা হয়েছে। দয়া করে আবার চেষ্টা করুন।');
    })
    .finally(() => {
      setLoadingQuestions(false);
    });
  }, [selectedChapter?.id, selectedQuestionType, phase]);

  const handleSubjectClick = (subject: Subject) => {
    setSelectedSubject(subject);
    setPhase('chapters');
  };
  const handleChapterClick = (chapter: any) => {
    if (chapter.is_locked) {
      if (onViewChange) {
        if (confirm('এই অধ্যায়টি প্রিমিয়াম। এটি অ্যাক্সেস করতে প্যাকেজ ক্রয় করতে হবে। তুমি কি পেমেন্ট পেজে যেতে চাও?')) {
          onViewChange('সাবস্ক্রিপশন');
        }
      } else {
        const pricingUrl = (window as any).shAuth?.pricing_url || '/pricing/';
        if (confirm('এই অধ্যায়টি প্রিমিয়াম। এটি অ্যাক্সেস করতে প্যাকেজ ক্রয় করতে হবে। তুমি কি পেমেন্ট পেজে যেতে চাও?')) {
          window.location.href = pricingUrl;
        }
      }
      return;
    }
    // Mark this chapter as visited for progress tracking
    if (selectedSubject) {
      markChapterVisited(selectedSubject.id, chapter.id);
    }
    setSelectedChapter(chapter);
    setPhase('questionType');
  };

  const handleStart = (qType: 'mcq' | 'cq') => {
    setSelectedQuestionType(qType);
    if (qType === 'cq') {
      setSelectedMode('unlimited');
      setPhase('practice');
      setSelectedAnswers({});
      setShowAnswers(false);
      setExpandedSubQuestions({});

      // Fire custom launch event
      const event = new CustomEvent('chorcaghor_launch_practice', {
        detail: {
          subject_id: selectedSubject?.id || '',
          chapter_id: selectedChapter?.id || '',
          mode_type: 'unlimited',
          question_type: 'cq'
        }
      });
      window.dispatchEvent(event);
    } else {
      // If MCQ: go to Mode Selection phase
      setSelectedMode(null); // Reset mode select state
      setPhase('mode');
    }
  };

  const handleStartPractice = () => {
    if (selectedMode === 'battle') {
      setPhase('battle');
      return;
    }
    setPhase('practice');
    setSelectedAnswers({});
    setShowAnswers(false);
    setExpandedSubQuestions({});

    // Fire custom launch event
    const event = new CustomEvent('chorcaghor_launch_practice', {
      detail: {
        subject_id: selectedSubject?.id || '',
        chapter_id: selectedChapter?.id || '',
        mode_type: selectedMode || 'quick',
        question_type: selectedQuestionType || 'mcq'
      }
    });
    window.dispatchEvent(event);
  };

  const toggleSubQuestion = (qId: number, label: string) => {
    const key = `${qId}-${label}`;
    setExpandedSubQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };



  const modes = [
    {
      id: 'quick',
      title: 'কুইক টেস্ট',
      desc: '২০টি এলোমেলো MCQ এবং ২০ মিনিটের সময়সীমা',
      icon: Zap,
      color: 'bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-900/30',
      activeColor: 'bg-amber-500 text-white border-amber-500',
    },
    {
      id: 'unlimited',
      title: 'আনলিমিটেড প্রাকটিস',
      desc: 'কোনো সময় বা সংখ্যা সীমাবদ্ধতা ছাড়া অনুশীলন',
      icon: Infinity,
      color: 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900/30',
      activeColor: 'bg-blue-500 text-white border-blue-500',
    },
    {
      id: 'battle',
      title: 'ব্যাটল',
      desc: 'রিয়েল-টাইম ১v১ কুইজ ম্যাচ এবং ইনস্ট্যান্ট ফিডব্যাক',
      icon: Swords,
      color: 'bg-purple-50 text-purple-600 border-purple-200 dark:bg-purple-950/20 dark:text-purple-400 dark:border-purple-900/30',
      activeColor: 'bg-purple-600 text-white border-purple-600',
    },
  ];

  // Build standard breadcrumbs segments
  const getBreadcrumbSegments = (): BreadcrumbSegment[] => {
    const segments: BreadcrumbSegment[] = [
      { label: 'বিষয় ভিত্তিক প্রস্তুতি', target: 'subjects' }
    ];
    if (phase !== 'subjects' && selectedSubject) {
      segments.push({ label: selectedSubject.name, target: 'chapters' });
    }
    if ((phase === 'questionType' || phase === 'mode' || phase === 'practice' || phase === 'battle') && selectedChapter) {
      segments.push({ label: selectedChapter.name, target: 'questionType' });
    }
    if (phase === 'mode') {
      segments.push({ label: selectedQuestionType === 'cq' ? 'CQ' : 'MCQ', target: 'mode' });
    }
    if (phase === 'practice') {
      segments.push({ label: selectedQuestionType === 'cq' ? 'CQ' : 'MCQ', target: 'practice' });
    }
    if (phase === 'battle') {
      segments.push({ label: 'ব্যাটল', target: 'battle' });
    }
    return segments;
  };

  const handleNavigate = (target: string) => {
    if (target === 'home') {
      onBack();
    } else if (target === 'subjects') {
      setPhase('subjects');
      setSelectedSubject(null);
      setSelectedChapter(null);
      setSelectedQuestionType(null);
      setSelectedMode(null);
    } else if (target === 'chapters') {
      setPhase('chapters');
      setSelectedChapter(null);
      setSelectedQuestionType(null);
      setSelectedMode(null);
    } else if (target === 'questionType') {
      setPhase('questionType');
      setSelectedQuestionType(null);
      setSelectedMode(null);
    } else if (target === 'mode') {
      setPhase('mode');
      setSelectedMode(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Dynamic Breadcrumbs */}
      {phase !== 'battle' && (
        <ShuddhoshikhaBreadcrumb 
          segments={getBreadcrumbSegments()} 
          onNavigate={handleNavigate} 
        />
      )}

      {contextLoading ? (
        <div className="space-y-4">
          <div>
            <div className="h-6 w-48 skel-shimmer rounded-lg mb-2" />
            <div className="h-4 w-72 skel-shimmer rounded-lg" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div
                key={idx}
                className="p-5 rounded-2xl bg-white dark:bg-[#1E293B]/60 border border-gray-100 dark:border-white/10 shadow-[0_4px_16px_rgba(0,0,0,0.02)] flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-2xl skel-shimmer flex-shrink-0" />
                <div className="flex-1 space-y-2 min-w-0">
                  <div className="h-4 w-2/3 skel-shimmer rounded-md" />
                  <div className="h-3 w-1/3 skel-shimmer rounded-md" />
                </div>
                <div className="w-9 h-9 rounded-full skel-shimmer flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Phase 1: Subject Selection */}
          {phase === 'subjects' && (
            <motion.div
              key="subjects"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <div>
                <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">বিষয় নির্বাচন করুন</h3>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">বিষয় ভিত্তিক প্রস্তুতির জন্য বিষয় বেছে নিন</p>
              </div>

              {!userOptional && (
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <p className="font-bangla text-sm text-amber-700">
                    আপনার অপশনাল সাবজেক্ট সিলেক্ট করুন প্রোফাইল থেকে
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {subjects.map((subject) => {
                  const Icon = iconMap[subject.icon] || BookOpen;
                  const progress = getSubjectProgress(subject.id, subject.chapters.length);
                  const ringColor = progress === 100 ? '#10b981' : '#00CBA9';
                  return (
                    <button
                      key={subject.id}
                      onClick={() => handleSubjectClick(subject)}
                      className="group p-5 rounded-2xl bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md border border-gray-100 dark:border-white/10 shadow-[0_4px_16px_rgba(0,0,0,0.02)] hover:border-[#00CBA9]/30 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 transition-all duration-300 text-left cursor-pointer"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-2xl bg-gradient-to-tr ${getSubjectGradient(subject.id)} flex items-center justify-center text-white shadow-lg border border-white/10 relative overflow-hidden flex-shrink-0`}>
                          <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />
                          <Icon className="w-5.5 h-5.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bangla text-sm font-semibold text-[#1F1F1F] dark:text-[#E8EAED] group-hover:text-[#00CBA9] transition-colors">
                            {subject.name}
                          </h4>
                          <p className="font-english text-xs text-gray-400 mt-1">
                            {subject.chapters.length} chapters
                          </p>
                        </div>
                        {/* Circular Progress Ring */}
                        <ProgressRing percentage={progress} color={ringColor} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Phase 2: Chapter Selection */}
          {phase === 'chapters' && selectedSubject && (
            <motion.div
              key="chapters"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <div>
                <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">{selectedSubject.name}</h3>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">অধ্যায় নির্বাচন করুন</p>
              </div>

              {/* বাংলা ২য় পত্র Split Tabs */}
              {selectedSubject.id === 'sub_bn2' && (
                <div className="flex border-b border-gray-200 dark:border-white/5 mb-4">
                  {(['ব্যাকরণ', 'নির্মিতি'] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`pb-2.5 px-4 font-bangla text-sm font-semibold border-b-2 transition-colors cursor-pointer ${
                        activeTab === tab
                          ? 'border-[#00CBA9] text-[#00CBA9] dark:text-[#8AB4F8]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-[#E8EAED]'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(selectedSubject.id === 'sub_bn2'
                  ? selectedSubject.chapters.filter((c) => c.tab === activeTab)
                  : selectedSubject.chapters
                ).map((chapter: any, index) => (
                  <button
                    key={chapter.id}
                    onClick={() => handleChapterClick(chapter)}
                    className="group flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md border border-gray-100 dark:border-white/10 hover:border-[#00CBA9]/30 hover:shadow-sm transition-all text-left cursor-pointer"
                  >
                    {/* Number box */}
                    <div className="w-10 h-10 rounded-xl bg-[#F4F7FB] dark:bg-white/5 flex items-center justify-center font-english text-sm font-bold text-[#00CBA9] flex-shrink-0">
                      {index + 1}
                    </div>

                    {/* Chapter name + questions */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bangla text-sm font-medium text-[#1F1F1F] dark:text-[#E8EAED] group-hover:text-[#00CBA9] transition-colors truncate">
                        {chapter.name}
                      </h4>
                      <p className="font-english text-xs text-gray-400 mt-0.5">
                        {chapter.totalQuestions} questions
                      </p>
                    </div>

                    {/* Chapter progress ring */}
                    {!chapter.is_locked && (() => {
                      const isVisited = chapterProgress[selectedSubject.id]?.has(chapter.id);
                      return (
                        <ProgressRing
                          percentage={isVisited ? 100 : 0}
                          color={isVisited ? '#00CBA9' : '#D1D5DB'}
                          size={32}
                        />
                      );
                    })()}

                    {/* Right icon */}
                    {chapter.is_locked ? (
                      <Lock className="w-4 h-4 text-red-500 flex-shrink-0" />
                    ) : chapter.is_paid ? (
                      <span className="text-[10px] bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded font-bangla font-semibold flex-shrink-0">আনলকড</span>
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#00CBA9] transition-colors flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Phase 3: Mode Selection */}
          {phase === 'mode' && selectedSubject && selectedChapter && (
            <motion.div
              key="mode"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-5"
            >
              <div>
                <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">মোড নির্বাচন করুন</h3>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">
                  {selectedSubject.name} - {selectedChapter.name}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {modes.map((mode) => {
                  const Icon = mode.icon;
                  const isActive = selectedMode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      onClick={() => setSelectedMode(mode.id)}
                      className={`p-5 rounded-2xl border-2 transition-all text-left cursor-pointer ${
                        isActive ? mode.activeColor : `${mode.color} hover:shadow-md`
                      }`}
                    >
                      <Icon className={`w-8 h-8 mb-3 ${isActive ? 'text-white' : ''}`} />
                      <h4 className={`font-bangla text-sm font-bold mb-1 ${isActive ? 'text-white' : ''}`}>
                        {mode.title}
                      </h4>
                      <p className={`font-bangla text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                        {mode.desc}
                      </p>
                    </button>
                  );
                })}
              </div>

              {selectedMode && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-end"
                >
                  <button
                    onClick={handleStartPractice}
                    className="flex items-center gap-2 px-8 py-3.5 rounded-2xl bg-[#00CBA9] text-white font-bangla text-sm font-medium hover:bg-[#00b898] transition-colors shadow-lg hover:shadow-xl cursor-pointer"
                  >
                    <Play className="w-4 h-4" />
                    শুরু করুন
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Phase 3.5: Question Type Selection */}
          {phase === 'questionType' && selectedSubject && selectedChapter && (
            <motion.div
              key="questionType"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-5"
            >
              <div>
                <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">প্রশ্নের ধরন নির্বাচন করুন</h3>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">
                  {selectedSubject.name} - {selectedChapter.name}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => handleStart('mcq')}
                  className="group p-6 rounded-2xl border border-gray-100 dark:border-white/5 bg-white dark:bg-[#1E1E1E]/60 hover:border-[#00CBA9] hover:shadow-md transition-all text-left cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[#F4F7FB] dark:bg-white/5 group-hover:bg-[#00CBA9]/10 transition-colors">
                      <FileQuestion className="w-7 h-7 text-gray-400 group-hover:text-[#00CBA9] transition-colors" />
                    </div>
                    <div>
                      <h4 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED] group-hover:text-[#00CBA9] transition-colors">MCQ</h4>
                      <p className="font-bangla text-sm text-gray-500">বহুনির্বাচনি প্রশ্নাবলী</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleStart('cq')}
                  className="group p-6 rounded-2xl border border-gray-100 dark:border-white/5 bg-white dark:bg-[#1E1E1E]/60 hover:border-[#00CBA9] hover:shadow-md transition-all text-left cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[#F4F7FB] dark:bg-white/5 group-hover:bg-[#00CBA9]/10 transition-colors">
                      <GraduationCap className="w-7 h-7 text-gray-400 group-hover:text-[#00CBA9] transition-colors" />
                    </div>
                    <div>
                      <h4 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED] group-hover:text-[#00CBA9] transition-colors">CQ</h4>
                      <p className="font-bangla text-sm text-gray-500">সৃজনশীল প্রশ্নাবলী</p>
                    </div>
                  </div>
                </button>
              </div>
            </motion.div>
          )}

          {/* Phase 4: Practice environment */}
          {phase === 'practice' && selectedSubject && selectedChapter && (
            selectedMode === 'quick' && selectedQuestionType === 'mcq' ? (
              loadingQuestions ? (
                <div className="flex flex-col items-center justify-center py-32 space-y-6 bg-white dark:bg-brand-dark-bg border border-gray-100 dark:border-white/5 rounded-3xl min-h-[400px]">
                  <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                  <div className="text-center space-y-2">
                    <h4 className="font-bangla text-lg font-bold text-slate-900 dark:text-white">কুইক টেস্ট তৈরি হচ্ছে...</h4>
                    <p className="font-bangla text-sm text-gray-500 dark:text-slate-400">প্রশ্নপত্র লোড হচ্ছে, অনুগ্রহ করে অপেক্ষা করুন।</p>
                  </div>
                </div>
              ) : fetchError ? (
                <div className="flex flex-col items-center justify-center py-32 space-y-6 bg-white dark:bg-brand-dark-bg border border-gray-100 dark:border-white/5 rounded-3xl min-h-[400px]">
                  <p className="font-bangla text-red-600 dark:text-red-400 font-semibold">{fetchError}</p>
                  <button 
                    onClick={() => {
                      const qType = selectedQuestionType;
                      setSelectedQuestionType(null);
                      setTimeout(() => setSelectedQuestionType(qType), 10);
                    }}
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-full text-sm font-bangla transition-colors cursor-pointer"
                  >
                    আবার চেষ্টা করুন
                  </button>
                </div>
              ) : (
                <ExamInterface
                  questionsBySubject={{ [selectedSubject.id]: examQuestions }}
                  subjectsData={[selectedSubject]}
                  duration={20}
                  negativeMarking={false}
                  onCancel={() => setPhase('mode')}
                  isDarkMode={isDarkMode}
                  chapterName={selectedChapter.name}
                />
              )
            ) : (
              <motion.div
                key="practice"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
              {/* Header Section */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <h3 className="font-bangla text-xl font-bold text-[#1F1F1F] dark:text-[#E8EAED]">
                    {selectedSubject.name} - {selectedChapter.name}
                  </h3>
                </div>

                {/* Show Answers Toggle */}
                <div className="flex items-center gap-3">
                  <span className="font-bangla text-sm font-semibold text-gray-600 dark:text-[#A0A5AD]">উত্তর দেখুন:</span>
                  <button
                    onClick={() => setShowAnswers(!showAnswers)}
                    className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none cursor-pointer ${
                      showAnswers ? 'bg-[#00CBA9] shadow-[0_0_10px_rgba(0,203,169,0.3)]' : 'bg-gray-200 dark:bg-white/10'
                    }`}
                  >
                    <span
                      className={`absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white dark:bg-[#121212] shadow-sm transform transition-transform duration-200 ${
                        showAnswers ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* MCQ & CQ Filters Bar */}
              {(selectedQuestionType === 'mcq' || selectedQuestionType === 'cq') && (
                <div className="flex flex-wrap items-center gap-3 py-1.5 border-b border-gray-100 dark:border-white/5 pb-5">
                  {/* Segment: সকল / বোর্ড */}
                  <div className="flex p-1 bg-gray-100 dark:bg-white/5 rounded-2xl items-center">
                    <button
                      onClick={() => setSelectedSegment('all')}
                      className={`px-4 py-2 font-bangla text-xs font-semibold rounded-xl transition-all cursor-pointer ${
                        selectedSegment === 'all'
                          ? 'bg-white dark:bg-[#1E1E1E] text-slate-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-slate-800'
                      }`}
                    >
                      সকল
                    </button>
                    <button
                      onClick={() => setSelectedSegment('board')}
                      className={`px-4 py-2 font-bangla text-xs font-semibold rounded-xl transition-all cursor-pointer ${
                        selectedSegment === 'board'
                          ? 'bg-white dark:bg-[#1E1E1E] text-slate-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-slate-800'
                      }`}
                    >
                      বোর্ড
                    </button>
                  </div>

                  {/* Dropdown: সকল টপিক */}
                  <div className="relative">
                    <select
                      value={selectedTopic}
                      onChange={(e) => setSelectedTopic(e.target.value)}
                      className="appearance-none font-bangla text-xs font-semibold bg-white dark:bg-[#1E1E1E]/60 border border-gray-200 dark:border-white/10 rounded-2xl pl-4 pr-10 py-3 text-slate-700 dark:text-slate-200 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#00CBA9] focus:border-[#00CBA9] shadow-sm transition-all"
                    >
                      <option value="all">সকল টপিক</option>
                      {availableTopics.map((topic) => (
                        <option key={topic} value={topic}>
                          {topic}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Dropdown: সকল সাল */}
                  <div className="relative">
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      className="appearance-none font-bangla text-xs font-semibold bg-white dark:bg-[#1E1E1E]/60 border border-gray-200 dark:border-white/10 rounded-2xl pl-4 pr-10 py-3 text-slate-700 dark:text-slate-200 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#00CBA9] focus:border-[#00CBA9] shadow-sm transition-all"
                    >
                      <option value="all">সকল সাল</option>
                      {availableYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}

              {/* Title Block */}
              <div className="border-b border-gray-100 dark:border-white/5 pb-4">
                <div className="w-20 h-1.5 bg-[#00CBA9] rounded-full mb-3" />
                <h3 className="font-bangla text-2xl font-bold text-[#1F1F1F] dark:text-[#E8EAED]">
                  {selectedQuestionType === 'cq' ? 'Practice CQ' : 'Practice MCQ'}
                </h3>
                <div className="flex items-center gap-1.5 mt-2">
                  <div className="w-2 h-2 rounded-full bg-[#00CBA9] animate-pulse" />
                  <span className="font-bangla text-xs text-[#00CBA9] font-medium">অনুশীলন করা হয়েছে</span>
                </div>
              </div>

              {/* Questions Loading / Empty State / List */}
              {loadingQuestions ? (
                <QuestionSkeleton qType={selectedQuestionType || 'mcq'} />
              ) : fetchError ? (
                <div className="bg-white dark:bg-[#1E1E1E]/85 dark:backdrop-blur-md dark:border-white/10 rounded-3xl p-10 text-center border border-gray-100 flex flex-col items-center justify-center space-y-4">
                  <p className="font-bangla text-red-600 dark:text-red-400 font-semibold">{fetchError}</p>
                  <button 
                    onClick={() => {
                      const qType = selectedQuestionType;
                      setSelectedQuestionType(null);
                      setTimeout(() => setSelectedQuestionType(qType), 10);
                    }}
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-full text-sm font-bangla transition-colors cursor-pointer"
                  >
                    আবার চেষ্টা করুন
                  </button>
                </div>
              ) : filteredQuestions.length === 0 ? (
                <div className="bg-white dark:bg-[#1E1E1E]/85 dark:backdrop-blur-md dark:border-white/10 rounded-3xl p-10 text-center border border-gray-100">
                  <p className="font-bangla text-gray-500">কোনো প্রশ্ন পাওয়া যায়নি।</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {paginatedQuestions.map((question, index) => {
                    const questionIndex = (currentPage - 1) * questionsPerPage + index + 1;
                    const isCQ = question.type === 'cq' || selectedQuestionType === 'cq';

                    if (isCQ) {
                      const parsedCQ = typeof question.question_text === 'object' && question.question_text !== null
                        ? question.question_text
                        : (() => {
                            try { return JSON.parse(question.question_text); } catch (e) { return {}; }
                          })();

                      return (
                        <div
                          key={question.id}
                          className="bg-white/60 dark:bg-[#1E1E1E]/40 backdrop-blur-xl dark:backdrop-blur-2xl border border-white/20 dark:border-white/10 rounded-3xl p-6 shadow-[0_8px_32px_rgba(31,38,135,0.04)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] hover:shadow-[0_8px_32px_rgba(31,38,135,0.06)] dark:hover:shadow-[0_8px_32px_rgba(0,0,0,0.3)] hover:border-[#00CBA9]/20 transition-all duration-300 space-y-5 text-left"
                        >
                          {/* Question Header */}
                          <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-4">
                             <div className="flex items-start gap-4 flex-1">
                               <div className="w-8 h-8 rounded-full bg-[#E8F0FE] dark:bg-[#00CBA9]/15 text-[#00CBA9] dark:text-[#8AB4F8] flex items-center justify-center font-english text-xs font-bold flex-shrink-0">
                                 {toBengaliNumber(questionIndex)}
                               </div>
                               <h4 className="font-bangla text-base sm:font-semibold font-normal text-[#1F1F1F] dark:text-[#E8EAED] leading-relaxed pt-0.5">
                                 সৃজনশীল প্রশ্ন {toBengaliNumber(questionIndex)}
                               </h4>
                             </div>
                             
                             {/* Flag / Fav Icons (no border, text-only icons for max space) */}
                             <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
                               <button 
                                 onClick={() => {
                                   (window as any).showReportModal?.(question.id);
                                 }}
                                 className={`p-1.5 rounded-xl transition-all duration-200 cursor-pointer bg-transparent border-none ${flaggedQuestionIds.has(question.id) ? 'text-[#00CBA9] bg-emerald-50 dark:bg-emerald-950/20' : 'text-gray-400 dark:text-gray-500 hover:text-[#00CBA9] hover:bg-teal-50 dark:hover:bg-teal-950/20'}`}
                                 title="রিপোর্ট করুন"
                               >
                                 <Flag className={`w-4 h-4 ${flaggedQuestionIds.has(question.id) ? 'text-[#00CBA9] fill-[#00CBA9]' : ''}`} />
                               </button>
                               <button 
                                 onClick={() => toggleFavorite({
                                   id: question.id,
                                   question_text: question.question_text || `সৃজনশীল প্রশ্ন ${questionIndex}`,
                                   options: [],
                                   correct_answer: "0",
                                   stem: question.stem,
                                   subject_id: selectedSubject?.id,
                                   subject_name: selectedSubject?.name,
                                   chapter_id: selectedChapter?.id
                                 })}
                                 className="p-1.5 transition-colors cursor-pointer bg-transparent border-none"
                               >
                                 <Heart className={`w-4 h-4 ${isFavorite(question.id) ? 'text-rose-500 fill-rose-500' : 'text-gray-400 dark:text-gray-500 hover:text-rose-500'}`} />
                               </button>
                             </div>
                           </div>

                          {/* Stem */}
                          {question.stem && (
                            <div className="p-5 rounded-2xl bg-[#F4F7FB] dark:bg-white/5 border-l-4 border-l-[#00CBA9] text-sm leading-relaxed text-[#1F1F1F] dark:text-[#E8EAED] font-bangla">
                              {question.stem}
                            </div>
                          )}

                          {/* Sub-questions */}
                          <div className="space-y-4 pl-4 md:pl-12">
                            {['ক', 'খ', 'গ', 'ঘ'].map((label) => {
                              const subQ = parsedCQ?.[label];
                              if (!subQ) return null;
                              const isExpanded = expandedSubQuestions[`${question.id}-${label}`] || showAnswers;

                              return (
                                <div key={label} className="space-y-2 border-l-2 border-gray-100 dark:border-white/5 pl-4 relative">
                                  <div className="absolute -left-2.5 top-0.5 w-5 h-5 rounded-full bg-[#F4F7FB] dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center font-bangla text-[10px] font-bold text-[#00CBA9]">
                                    {label}
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                                    <p className="font-bangla text-sm text-[#1F1F1F] dark:text-[#E8EAED] leading-relaxed">
                                      {subQ.text} <span className="text-xs text-gray-400 dark:text-gray-500 font-semibold font-bangla ml-1">(মান: {subQ.marks})</span>
                                    </p>
                                    <button
                                      onClick={() => toggleSubQuestion(question.id, label)}
                                      className="text-xs text-[#00CBA9] font-bangla font-semibold hover:underline bg-transparent border-none cursor-pointer flex-shrink-0 self-start sm:self-center"
                                    >
                                      {isExpanded ? 'উত্তর লুকান' : 'উত্তর দেখুন'}
                                    </button>
                                  </div>
                                  {isExpanded && subQ.sub_question_answer && (
                                    <motion.div
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: 'auto' }}
                                      className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 text-xs text-amber-700 dark:text-amber-300 font-bangla leading-relaxed"
                                    >
                                      <strong>নমুনা উত্তর:</strong> {subQ.sub_question_answer}
                                    </motion.div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Card Footer */}
                          <div className="flex items-center justify-between border-t border-gray-50 dark:border-white/5 pt-4 pl-0 md:pl-12 text-xs text-gray-400 dark:text-[#A0A5AD]">
                             <span className="font-english font-medium">
                               {question.exam_year || '2025'}
                             </span>
                           </div>

                           {/* Explanation */}
                           {showAnswers && question.explanation && (
                             <div className="mt-3 p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 text-xs text-amber-700 dark:text-amber-300 pl-4 md:pl-12 leading-relaxed">
                               <strong>ব্যাখ্যা:</strong> {question.explanation}
                             </div>
                           )}
                        </div>
                      );
                    }

                    const selectedOpt = selectedAnswers[question.id];
                    const hasAnswered = selectedOpt !== undefined;
                    const correctOpt = normalizeCorrectAnswer(question.correct_answer);

                    return (
                      <div
                        key={question.id}
                        className="bg-white/60 dark:bg-[#1E1E1E]/40 backdrop-blur-xl dark:backdrop-blur-2xl border border-white/20 dark:border-white/10 rounded-3xl p-6 shadow-[0_8px_32px_rgba(31,38,135,0.04)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] hover:shadow-[0_8px_32px_rgba(31,38,135,0.06)] dark:hover:shadow-[0_8px_32px_rgba(0,0,0,0.3)] hover:border-[#00CBA9]/20 transition-all duration-300 space-y-5 text-left"
                      >
                        <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="flex items-start gap-4 flex-1">
                            {/* Index Box with Green Dot */}
                            <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-[#8AB4F8] flex items-center justify-center font-english text-xs font-bold relative flex-shrink-0">
                              {toBengaliNumber(questionIndex)}
                              <div className="w-2 h-2 rounded-full bg-[#00CBA9] absolute -top-0.5 -right-0.5 border border-white dark:border-[#1E1E1E]" />
                            </div>
                            <div className="flex-1 space-y-1.5">
                              <h4 className="font-bangla text-base sm:font-semibold font-normal text-[#1F1F1F] dark:text-[#E8EAED] leading-relaxed pt-0.5 whitespace-pre-line">
                                {question.question_text}
                              </h4>
                            </div>
                          </div>
                          
                          {/* Flag / Fav Icons (no border, text-only icons for max space) */}
                          <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
                             <button 
                               onClick={() => (window as any).showReportModal?.(question.id)}
                               className={`p-1.5 rounded-xl transition-all duration-200 cursor-pointer bg-transparent border-none ${flaggedQuestionIds.has(question.id) ? 'text-[#00CBA9] bg-emerald-50 dark:bg-emerald-950/20' : 'text-slate-400 hover:text-[#00CBA9] hover:bg-teal-50 dark:hover:bg-teal-950/20'}`}
                               title="রিপোর্ট করুন"
                             >
                               <Flag className={`w-4 h-4 ${flaggedQuestionIds.has(question.id) ? 'text-[#00CBA9] fill-[#00CBA9]' : ''}`} />
                             </button>
                            <button 
                              onClick={() => toggleFavorite({
                                id: question.id,
                                question_text: question.question_text,
                                options: question.options,
                                correct_answer: question.correct_answer,
                                explanation: question.explanation,
                                stem: question.stem,
                                reference: question.reference,
                                subject_id: selectedSubject?.id,
                                subject_name: selectedSubject?.name,
                                chapter_id: selectedChapter?.id
                              })}
                              className="p-1.5 transition-colors cursor-pointer bg-transparent border-none"
                            >
                              <Heart className={`w-4 h-4 ${isFavorite(question.id) ? 'text-rose-500 fill-rose-500' : 'text-gray-400 dark:text-gray-500 hover:text-rose-500'}`} />
                            </button>
                          </div>
                        </div>

                        {/* Options */}
                        <div className="grid grid-cols-1 gap-3 pl-0 md:pl-12">
                          {question.options && question.options.map((option: string, optionIndex: number) => {
                            const isSelected = selectedOpt === optionIndex;
                            const isCorrect = optionIndex === correctOpt;

                             let optionStyle = 'border-gray-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:border-[#00CBA9]/30 dark:hover:border-white/15 hover:bg-white dark:hover:bg-white/10 hover:shadow-sm';
                             let badgeStyle = 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400';

                            if (showAnswers || hasAnswered) {
                              if (isCorrect) {
                                optionStyle = 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
                                badgeStyle = 'bg-emerald-550 text-white';
                              } else if (isSelected) {
                                optionStyle = 'border-rose-500 bg-rose-500/10 text-rose-700 dark:text-rose-400';
                                badgeStyle = 'bg-rose-500 text-white';
                              }
                            } else if (isSelected) {
                              optionStyle = 'border-[#00CBA9] bg-[#00CBA9]/5 text-[#00CBA9] dark:text-[#8AB4F8]';
                              badgeStyle = 'bg-[#00CBA9] text-white';
                            }

                            return (
                              <button
                                key={optionIndex}
                                onClick={() => {
                                  if (!showAnswers && !hasAnswered) {
                                    setSelectedAnswers({ ...selectedAnswers, [question.id]: optionIndex });

                                    // Asynchronous non-blocking sync to the database
                                    const optionsLabels = ['ক', 'খ', 'গ', 'ঘ'];
                                    const isCorrect = optionIndex === correctOpt;
                                    
                                    const mapFrontendToDb = (id: string): string => {
                                      const map: Record<string, string> = {
                                        'sub_bn1': 'bangla', 'sub_bn2': 'bangla-2nd', 'sub_eng1': 'english', 'sub_eng2': 'english-2nd',
                                        'sub_math': 'general-math', 'sub_ict': 'ict', 'sub_religion': 'religion', 'sub_phy': 'physics',
                                        'sub_chem': 'chemistry', 'sub_bio': 'biology', 'sub_hmath': 'higher-math', 'sub_hist': 'history',
                                        'sub_geo': 'geography', 'sub_civ': 'civics', 'sub_gsci': 'general-science', 'sub_acc': 'accounting',
                                        'sub_fin': 'finance', 'sub_bus': 'business', 'sub_eco': 'economics', 'opt_agri': 'agriculture',
                                        'opt_home': 'home-science'
                                      };
                                      return map[id] || id;
                                    };
                                    const dbSubject = selectedSubject?.id ? mapFrontendToDb(selectedSubject.id) : 'general';

                                    const headers: Record<string, string> = {
                                      'Content-Type': 'application/json'
                                    };
                                    if (shAuth?.rest_nonce) {
                                      headers['X-WP-Nonce'] = shAuth.rest_nonce;
                                    }

                                    fetch('/wp-json/shuddhoshikha/v1/sync-history', {
                                      method: 'POST',
                                      headers: headers,
                                      body: JSON.stringify({
                                        action: 'sync_practice_answer',
                                        question_id: question.id,
                                        chosen_option: optionsLabels[optionIndex],
                                        correct_option: correctOpt !== undefined && correctOpt !== null ? optionsLabels[correctOpt] : null,
                                        is_correct: isCorrect,
                                        subject_category: dbSubject
                                      }),
                                      keepalive: true
                                    }).catch(err => console.error('Failed to sync practice answer:', err));
                                  }
                                }}
                                className={`group flex items-center p-3.5 rounded-2xl border transition-all text-left w-full cursor-pointer ${optionStyle}`}
                              >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bangla text-xs font-bold mr-3.5 flex-shrink-0 transition-colors ${badgeStyle}`}>
                                  {['ক', 'খ', 'গ', 'ঘ'][optionIndex]}
                                </div>
                                <span className="font-bangla text-sm sm:font-medium font-normal leading-relaxed">
                                  {option}
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        {/* Card Footer */}
                        <div className="flex items-center justify-between border-t border-gray-50 dark:border-white/5 pt-4 pl-0 md:pl-12 text-xs text-gray-400 dark:text-[#A0A5AD]">
                          <span className="font-english font-medium">
                            {question.exam_year || '2025'}
                          </span>
                        </div>

                        {/* Explanation */}
                        {(showAnswers || hasAnswered) && question.explanation && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-3 p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 text-xs text-amber-700 dark:text-amber-300 pl-4 md:pl-12 leading-relaxed"
                          >
                            <strong>ব্যাখ্যা:</strong> {question.explanation}
                          </motion.div>
                        )}
                      </div>
                    );
                  })}

                  {/* Pagination Controls */}
                  {selectedMode === 'unlimited' && totalPages > 1 && (
                    <div className="flex items-center justify-center gap-4 py-6 border-t border-b border-gray-100 dark:border-white/5 my-6">
                      {/* Previous button */}
                      <button
                        disabled={currentPage === 1}
                        onClick={() => {
                          setCurrentPage(prev => Math.max(1, prev - 1));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bangla font-bold transition-all border cursor-pointer ${
                          currentPage === 1
                            ? 'bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-gray-600 border-transparent cursor-not-allowed'
                            : 'bg-white dark:bg-[#1E1E1E] border-gray-200 dark:border-white/10 text-slate-700 dark:text-slate-200 hover:border-[#00CBA9] shadow-sm hover:shadow'
                        }`}
                      >
                        ← পেছনে
                      </button>

                      {/* Page Info Display */}
                      <div className="px-5 py-2.5 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5 text-slate-700 dark:text-slate-300 font-bangla text-sm font-bold shadow-inner">
                        পৃষ্ঠা {toBengaliNumber(currentPage)} এর {toBengaliNumber(totalPages)} (মোট {toBengaliNumber(filteredQuestions.length)}টি)
                      </div>

                      {/* Next button */}
                      <button
                        disabled={currentPage === totalPages}
                        onClick={() => {
                          setCurrentPage(prev => Math.min(totalPages, prev + 1));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bangla font-bold transition-all border cursor-pointer ${
                          currentPage === totalPages
                            ? 'bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-gray-600 border-transparent cursor-not-allowed'
                            : 'bg-white dark:bg-[#1E1E1E] border-gray-200 dark:border-white/10 text-slate-700 dark:text-slate-200 hover:border-[#00CBA9] shadow-sm hover:shadow'
                        }`}
                      >
                        পরবর্তী →
                      </button>
                    </div>
                  )}

                  {/* Back to Chapters */}
                  <div className="flex justify-end pt-4">
                    <button
                      onClick={() => setPhase('questionType')}
                      className="px-8 py-3.5 rounded-2xl bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 text-[#1F1F1F] dark:text-white font-bangla text-sm font-bold transition-all cursor-pointer"
                    >
                      অনুশীলন শেষ করুন
                    </button>
                  </div>
                </div>
              )}
              </motion.div>
            )
          )}
          {phase === 'battle' && selectedSubject && selectedChapter && (
            <BattleArena
              questions={questions}
              chapterName={selectedChapter.name}
              subjectName={selectedSubject.name}
              onClose={() => {
                if (isSharedLink) {
                  setPhase('subjects');
                  setSelectedSubject(null);
                  setSelectedChapter(null);
                  setIsSharedLink(false);
                } else {
                  setPhase('mode');
                }
              }}
              isDarkMode={isDarkMode}
              userProfile={{
                name: (window as any).shAuth?.user?.name || (profile as any)?.name || (profile as any)?.display_name || 'Shajjad Islam',
                avatar: (window as any).shAuth?.user?.avatar || (profile as any)?.avatar || '👱‍♂️'
              }}
            />
          )}
          {phase !== 'practice' && phase !== 'battle' && (
            <div className="flex justify-start pt-6 md:hidden">
              <button
                onClick={() => {
                  if (phase === 'subjects') {
                    onBack();
                  } else if (phase === 'chapters') {
                    handleNavigate('subjects');
                  } else if (phase === 'questionType') {
                    handleNavigate('chapters');
                  } else if (phase === 'mode') {
                    handleNavigate('questionType');
                  } else {
                    onBack();
                  }
                }}
                className="flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-white dark:bg-[#1E1E1E]/60 border border-gray-200 dark:border-white/5 text-gray-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 hover:border-gray-300 dark:hover:border-white/10 active:scale-95 transition-all cursor-pointer font-bangla text-sm font-bold shadow-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>পেছনে যান</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
